/**
 * PairToBondCalculator - PB Arrow Type
 *
 * Handles arrows from lone pairs to bonds
 * Common in nucleophilic addition or substitution mechanisms
 */

import {
  ArrowCalculator,
  type ArrowCalculationConfig,
  type ArrowEndpoints,
} from "./types";
import {
  defaultLonePairPositioner,
  type LonePairPositioner,
} from "../positioning/LonePairPositioner";
import {
  defaultBondPositioner,
  type BondPositioner,
} from "../positioning/BondPositioner";
import { blendDirections } from "../core/geometry";
import { ENDPOINT_CONSTANTS } from "../core/constants";

/**
 * Calculator for Pair-to-Bond (PB) arrows
 *
 * Source: Lone pair
 * Destination: Bond
 */
export class PairToBondCalculator extends ArrowCalculator {
  private lpPositioner: LonePairPositioner;
  private bondPositioner: BondPositioner;

  constructor(
    lpPositioner: LonePairPositioner = defaultLonePairPositioner,
    bondPositioner: BondPositioner = defaultBondPositioner
  ) {
    super();
    this.lpPositioner = lpPositioner;
    this.bondPositioner = bondPositioner;
  }

  getTypeName(): string {
    return "Pair→Bond (PB)";
  }

  calculate(config: ArrowCalculationConfig): ArrowEndpoints {
    const { source, destination, cameraDir } = config;

    // Validate types
    if (source.type !== "lonepair") {
      throw new Error(
        `PairToBondCalculator expects source type 'lonepair', got '${source.type}'`
      );
    }
    if (destination.type !== "bond") {
      throw new Error(
        `PairToBondCalculator expects destination type 'bond', got '${destination.type}'`
      );
    }

    let endPos = destination.position;
    const atomPos = source.position;

    // Handle fish arrow (shortens to midpoint if not adjacent)
    const fishResult = this.handleFishArrow(config, atomPos, endPos);
    endPos = fishResult.endPos;

    // Step 1: Calculate start direction from lone pair
    const initialStartDir = this.lpPositioner.calculateStartDirection(
      source,
      endPos
    );

    // Step 2: Calculate start position
    const direction = this.lpPositioner.selectAndAdjustDirection(
      source,
      endPos
    );
    let startPos = this.lpPositioner.calculateStartPoint(source, direction);

    // Step 3: Calculate end direction (perpendicular to bond)
    let endDir = this.bondPositioner.calculateDirection(
      destination,
      startPos,
      cameraDir
    );

    // Step 4: Blend directions for smooth curve
    const startDir = blendDirections(
      initialStartDir,
      endDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Recalculate start position with blended direction
    startPos = this.lpPositioner.calculateStartPoint(source, direction);

    // Re-blend end direction
    endDir = blendDirections(
      endDir,
      initialStartDir,
      ENDPOINT_CONSTANTS.DIRECTION_BLEND_WEIGHT
    );

    // Step 5: Calculate final endpoint on bond
    const p3 = this.bondPositioner.calculateEndPoint(
      destination,
      startPos,
      endDir,
      cameraDir
    );

    return {
      startPos,
      endPos,
      p3,
      startDir,
      endDir,
      isDestAdjacent: config.isDestAdjacent,
    };
  }
}

/**
 * Default singleton instance
 */
export const defaultPairToBondCalculator = new PairToBondCalculator();
